(function () {
  const FEATURED_ROOT_SELECTOR = '[data-crossupsell-featured-root]';
  const CONFIG_SELECTOR = '[data-crossupsell-config]';
  const LOG_PREFIX = '[crossupsell]';


  function normalizeText(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeSku(value) {
    return normalizeText(value);
  }

  function normalizePrice(value) {
    const raw = String(value ?? '').trim();
    if (raw === '') return 0;

    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return 0;

    if (raw.includes('.')) return numeric;

    return Math.abs(numeric) >= 1000 ? numeric / 100 : numeric;
  }

  function formatMoney(value, moneyFormat) {
    const amountValue = normalizePrice(value);
    const amount = amountValue.toFixed(2);

    if (!moneyFormat) return `$${amount}`;

    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amountValue)));
  }

  function getProductHandle(product) {
    const explicitHandle = String(product?.handle || '').trim();
    if (explicitHandle) return explicitHandle;

    const url = String(product?.url || '').trim();
    const match = url.match(/\/products\/([^/?#]+)/i);
    return match?.[1] ? decodeURIComponent(match[1]).trim() : '';
  }

  function getProductUrl(product, config) {
    const explicitUrl = String(product?.url || '').trim();
    if (explicitUrl) return explicitUrl;

    const handle = getProductHandle(product);
    if(config?.locale == 'fr'){
      return handle ? `/fr/products/${encodeURIComponent(handle)}` : '';
    }else{
      return handle ? `/products/${encodeURIComponent(handle)}` : '';
    }
  }

  function getPriceData(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant && variant.available === true);
    const sourceVariants = availableVariants.length ? availableVariants : variants;

    const lowestVariant = sourceVariants.reduce((lowest, variant) => {
      if (!variant) return lowest;
      if (!lowest) return variant;
      return normalizePrice(variant.price) < normalizePrice(lowest.price) ? variant : lowest;
    }, null);

    const fallbackPrice = normalizePrice(product?.price);
    const price = lowestVariant ? normalizePrice(lowestVariant.price) : fallbackPrice;
    const compareAt = lowestVariant
      ? normalizePrice(lowestVariant.compare_at_price)
      : normalizePrice(product?.compare_at_price);

    return {
      price,
      compareAt,
      onSale: compareAt > price,
    };
  }

  function getAvailableVariants(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.filter((variant) => variant && variant.available === true);
  }

  function getAllVariants(product) {
    return Array.isArray(product?.variants) ? product.variants.filter(Boolean) : [];
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function createCard(product, config, options = {}) {
    const { featured = false } = options;
    const card = document.createElement('article');
    card.className = `crossupsell-card${featured ? ' crossupsell-card--featured' : ''} swiper-slide`;

    const imageUrl = product.featured_image || product.images?.[0]?.src || '';
    const productUrl = getProductUrl(product, config);
    const { compareAt, price, onSale } = getPriceData(product);
    const variants = getAllVariants(product);
    const availableVariants = getAvailableVariants(product);
    const initialVariant = availableVariants[0] || null;
    const sizeValue = initialVariant?.public_title || initialVariant?.title || '';
    const optionsMarkup = variants.map((variant) => {
      const label = variant.public_title || variant.title;
      return `
        <button
          type="button"
          class="crossupsell-option-box${variant.available ? '' : ' is-unavailable'}"
          data-crossupsell-option-box
          data-variant-id="${variant.id}"
          data-size-label="${escapeHtml(label)}"
          ${variant.available ? '' : 'aria-disabled="true" tabindex="-1"'}
        >
          ${escapeHtml(label)}
        </button>
      `;
    }).join('');
    const hasAvailableVariants = availableVariants.length > 0;
    const selectMarkup = hasAvailableVariants
      ? `
        <!--<label class="crossupsell-option-label" for="crossupsell-option-${product.id}">
          ${config.selectSizeLabel || config.sizeLabel || 'Size'}
        </label>-->
        <div
          id="crossupsell-option-${product.id}"
          class="crossupsell-option-grid"
          role="list"
        >${optionsMarkup}</div>
      `
      : `<span class="crossupsell-unavailable">${config.unavailableLabel || 'Unavailable'}</span>`;

    card.innerHTML = featured
      ? `
        <a href="${productUrl}" class="crossupsell-image-wrap">
          ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : ''}
        </a>
        <div class="crossupsell-content">
          <h5 class="crossupsell-title"><a href="${productUrl}">${product.title}</a></h5>
          <div class="crossupsell-price${onSale ? ' is-sale' : ''}">
            <span class="${onSale ? `sale` : `` }">${formatMoney(price, config.moneyFormat)}</span>
            ${onSale ? `<s>${formatMoney(compareAt, config.moneyFormat)}</s>` : ''}
          </div>
          <div class="crossupsell-size">
            <span class="crossupsell-size__label">${config.sizeLabel || 'Size'}:</span>
            <span class="crossupsell-size__value" data-crossupsell-size-value>${escapeHtml(sizeValue)}</span>
          </div>
          <div class="crossupsell-options">${selectMarkup}</div>
          <button
            type="button"
            class="crossupsell-atc btn btn--primary btn--full"
            data-crossupsell-atc
            data-variant-id="${initialVariant?.id || ''}"
            ${hasAvailableVariants ? '' : 'disabled'}
          >
            ${hasAvailableVariants ? (config.addToCartLabel || '') : (config.unavailableLabel || 'Unavailable')}
          </button>
        </div>
      `
      : `
        <a href="${productUrl}" class="crossupsell-image-wrap">
          ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : ''}
        </a>
        <h5 class="crossupsell-title"><a href="${productUrl}">${product.title}</a></h5>
        <div class="crossupsell-price${onSale ? ' is-sale' : ''}">
          <span>${formatMoney(price, config.moneyFormat)}</span>
          ${onSale ? `<s>${formatMoney(compareAt, config.moneyFormat)}</s>` : ''}
        </div>
      `;

    return card;
  }

  async function addVariantToCart(variantId) {
    if (!variantId) return null;

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add variant ${variantId} to cart.`);
    }

    return response.json();
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to fetch cart.');
    }

    return response.json();
  }

  function dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    document.dispatchEvent(new CustomEvent('quick-cart:updated'));
    document.dispatchEvent(new Event('apps:product-added-to-cart'));
  }

  function dispatchCartItemAdded(addedItem) {
    if (!addedItem) return;
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: addedItem } }));
  }

  function openQuickCartLikeNativeProductFlow() {
    const cartIcon = document.querySelector('[data-js-cart-icon]');

    if (cartIcon) {
      cartIcon.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return;
    }

    document.dispatchEvent(new CustomEvent('quick-cart:open'));
  }

  function openQuickCartAfterOpenSignal() {
    const QUICK_CART_OPEN_DELAY_MS = 300;
    const QUICK_CART_OPEN_FALLBACK_MS = 800;
    return new Promise((resolve) => {
      const quickCartWrapper =
        document.querySelector('.quick-cart__wrapper') || document.querySelector('.quick-cart');
      let completed = false;

      const complete = () => {
        if (completed) return;
        completed = true;
        clearTimeout(timeoutId);
        observer?.disconnect();
        resolve();
      };

      const observer = quickCartWrapper
        ? new MutationObserver(() => {
            if (quickCartWrapper.classList.contains('active')) {
              complete();
            }
          })
        : null;

      if (observer && quickCartWrapper) {
        observer.observe(quickCartWrapper, { attributes: true, attributeFilter: ['class'] });
      }

      const timeoutId = setTimeout(complete, QUICK_CART_OPEN_FALLBACK_MS);
      setTimeout(() => {
        openQuickCartLikeNativeProductFlow();
      }, QUICK_CART_OPEN_DELAY_MS);
    });
  }

  function getCrossSellProductHandles(mappings, currentProductVariantSkus = []) {
    const normalizedCurrentSkus = Array.isArray(currentProductVariantSkus)
      ? [...new Set(currentProductVariantSkus.map(normalizeSku).filter(Boolean))]
      : [];

    if (!normalizedCurrentSkus.length) {
      console.warn(`${LOG_PREFIX} current product variant SKUs are missing.`);
      return [];
    }

    if (!Array.isArray(mappings)) {
      console.warn(`${LOG_PREFIX} mapping payload is not an array.`, { mappings });
      return [];
    }

    const handles = mappings
      .filter((entry) => normalizedCurrentSkus.includes(normalizeSku(entry?.variant_sku)))
      .flatMap((entry) => (Array.isArray(entry?.cross_sell_products) ? entry.cross_sell_products : []))
      .map((item) => String(item?.handle || '').trim())
      .filter(Boolean);

    if (!handles.length) {
      console.warn(`${LOG_PREFIX} no cross-sell mapping matched current product variant SKUs.`, {
        currentProductVariantSkus,
      });
      return [];
    }

    return [...new Set(handles)];
  }

  async function fetchProductsByHandles(handles, config = {}) {
    if (!handles.length) return [];

    const localePrefix = config?.locale === 'fr' ? '/fr' : '';

    const requests = handles.map(async (handle) => {
      const url = `${localePrefix}/products/${encodeURIComponent(handle)}.js`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.warn(`${LOG_PREFIX} failed to fetch product by handle.`, {
            handle,
            url,
            status: response.status,
            statusText: response.statusText,
          });
          return null;
        }

        const product = await response.json();
        return product || null;
      } catch (error) {
        console.warn(`${LOG_PREFIX} error fetching product by handle.`, { handle, url, error });
        return null;
      }
    });

    const results = await Promise.all(requests);
    return results.filter(Boolean);
  }

  function isRenderableProduct(product, config) {
    const resolvedHandle = getProductHandle(product);
    const resolvedUrl = getProductUrl(product);
    const productId = String(product?.id || '').trim();

    if (!resolvedHandle && !resolvedUrl) return false;

    if (config.currentProductId && productId && productId === String(config.currentProductId)) {
      return false;
    }

    if (
      resolvedHandle
      && config.productHandle
      && normalizeText(resolvedHandle) === normalizeText(config.productHandle)
    ) {
      return false;
    }

    const topLevelAvailable = Boolean(product?.available);
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const hasAvailableVariant = variants.some((variant) => variant && variant.available === true);
    return variants.length > 0 ? hasAvailableVariant : topLevelAvailable;
  }

  async function getRenderableProducts(config) {
    const mappingResponse = await fetch(config.crossSellsAssetUrl);
    if (!mappingResponse.ok) {
      console.warn(`${LOG_PREFIX} failed to fetch cross-sell mappings.`, {
        mappingUrl: config.crossSellsAssetUrl,
        status: mappingResponse.status,
        statusText: mappingResponse.statusText,
      });
      return [];
    }

    const mappings = await mappingResponse.json();
    const productHandles = getCrossSellProductHandles(
      mappings,
      config.currentProductVariantSkus || [config.currentVariantSku],
    );
    if (!productHandles.length) return [];

    const products = await fetchProductsByHandles(productHandles, config);
    return products.filter((product) => isRenderableProduct(product, config));
  }

  function initFeaturedSwiper(featuredRoot) {
    const container = featuredRoot.querySelector('[data-crossupsell-featured-grid]');
    const wrapper = featuredRoot.querySelector('[data-crossupsell-featured-wrapper]');
    if (!container || !wrapper) return;

    if (container.crossupsellSlider && typeof container.crossupsellSlider.destroy === 'function') {
      container.crossupsellSlider.destroy();
    }

    const slides = Array.from(container.querySelectorAll('.swiper-slide'));
    const slideCount = slides.length;
    const prevButton = featuredRoot.querySelector('[data-prev]');
    const nextButton = featuredRoot.querySelector('[data-next]');

    if (slideCount <= 1) {
      if (prevButton) prevButton.classList.add('swiper-button-disabled');
      if (nextButton) nextButton.classList.add('swiper-button-disabled');
      wrapper.style.transform = '';
      return;
    }

    let currentIndex = 0;

    function update() {
      wrapper.style.transform = `translate3d(${-currentIndex * 100}%, 0, 0)`;

      if (prevButton) {
        prevButton.classList.toggle('swiper-button-disabled', currentIndex === 0);
      }

      if (nextButton) {
        nextButton.classList.toggle('swiper-button-disabled', currentIndex >= slideCount - 1);
      }
    }

    function onPrevClick() {
      if (currentIndex === 0) return;
      currentIndex -= 1;
      update();
    }

    function onNextClick() {
      if (currentIndex >= slideCount - 1) return;
      currentIndex += 1;
      update();
    }

    if (prevButton) prevButton.addEventListener('click', onPrevClick);
    if (nextButton) nextButton.addEventListener('click', onNextClick);

    container.crossupsellSlider = {
      destroy() {
        if (prevButton) prevButton.removeEventListener('click', onPrevClick);
        if (nextButton) nextButton.removeEventListener('click', onNextClick);
      },
    };

    update();
  }

  async function initCrossupsells() {
    const featuredRoot = document.querySelector(FEATURED_ROOT_SELECTOR);
    const configEl = document.querySelector(CONFIG_SELECTOR);

    if (!featuredRoot || !configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error(`${LOG_PREFIX} failed to parse config JSON.`, { error });
      return;
    }

    if (!config?.currentVariantSku || !config?.crossSellsAssetUrl) {
      return;
    }

    let renderableProducts = [];
    try {
      renderableProducts = await getRenderableProducts(config);
    } catch (error) {
      console.error(`${LOG_PREFIX} failed while loading renderable products.`, { error });
      return;
    }

    if (!renderableProducts.length) return;

    const featuredGrid = featuredRoot.querySelector('[data-crossupsell-featured-wrapper]');
    if (!featuredGrid) return;

    featuredGrid.innerHTML = '';
    renderableProducts.forEach((product) => {
      featuredGrid.appendChild(createCard(product, config, { featured: true }));
    });

    featuredGrid.querySelectorAll('.crossupsell-card--featured').forEach((card) => {
      const optionButtons = Array.from(card.querySelectorAll('[data-crossupsell-option-box]'));
      const sizeValueEl = card.querySelector('[data-crossupsell-size-value]');
      const atcButton = card.querySelector('[data-crossupsell-atc]');
      if (!atcButton) return;

      if (optionButtons.length && sizeValueEl) {
        optionButtons.forEach((button) => {
          button.addEventListener('click', () => {
            if (button.classList.contains('is-unavailable')) return;

            optionButtons.forEach((btn) => btn.classList.remove('is-active'));
            button.classList.add('is-active');

            const selectedSize = button.dataset.sizeLabel || '';
            const selectedVariantId = button.dataset.variantId || '';
            sizeValueEl.textContent = selectedSize;
            atcButton.dataset.variantId = selectedVariantId;
          });
        });

        const initialButton = optionButtons.find((button) => !button.classList.contains('is-unavailable'));
        if (initialButton) {
          initialButton.classList.add('is-active');
        }
      }

      atcButton.addEventListener('click', async () => {
        if (atcButton.disabled) return;
        const variantId = atcButton.dataset.variantId;
        if (!variantId) return;

        const initialLabel = atcButton.textContent;
        atcButton.disabled = true;

        try {
          const addedItem = await addVariantToCart(variantId);
          const cart = await fetchCart();
          dispatchCartItemAdded(addedItem);
          dispatchCartUpdated(cart);
          await openQuickCartAfterOpenSignal();
          atcButton.textContent = config.addedToCartLabel || config.addToCartLabel || initialLabel;
          setTimeout(() => {
            atcButton.textContent = initialLabel;
          }, 1200);
        } catch (error) {
          console.warn(`${LOG_PREFIX} failed to add cross-sell variant to cart.`, { variantId, error });
        } finally {
          atcButton.disabled = false;
        }
      });
    });

    featuredRoot.hidden = false;
    initFeaturedSwiper(featuredRoot);
  }

  function run() {
    if (document.documentElement.dataset.crossupsellInitialized === 'true') return;
    document.documentElement.dataset.crossupsellInitialized = 'true';
    initCrossupsells();
  }

  document.addEventListener('DOMContentLoaded', run);
  document.addEventListener('shopify:section:load', run);

   setInterval(function(){
    
    }, 500);
})();
